import type { CSSProperties } from "react";
import type { ReactNode } from "react";

import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export type ReportsViewWidgetPairingOption = {
  id: string;
  label: string;
  description: string;
};

const BAR_CHART_PREVIEW_HEIGHTS = [
  28, 44, 24, 56, 36, 48, 32, 60, 40, 52, 30, 46, 34, 58, 38, 50, 26, 42, 54,
  32,
];

const PREVIEW_HEIGHT = 56;

/**
 * How big a drawing of a chart is, and whether it moves.
 *
 * The same shapes serve a picker the width of a widget and a tile the size of
 * a stamp, so everything that has a length is written once here and read off
 * by scale. Small, it holds still: a page of report cards each breathing
 * through four little charts is a page nobody can read.
 */
const PREVIEW_SCALE = {
  full: {
    /** Shared thickness for bars, rows, and strokes across preview types. */
    stroke: 6,
    pie: PREVIEW_HEIGHT - 8,
    bars: BAR_CHART_PREVIEW_HEIGHTS,
    rowGap: 0.35,
    rowsWidth: "72%",
    plays: "running",
  },
  compact: {
    stroke: 5,
    pie: 38,
    // Every third bar, at half the height: the same skyline, drawn at a size
    // where twenty bars would be a smear.
    bars: BAR_CHART_PREVIEW_HEIGHTS.filter((_, index) => index % 3 === 0).map(
      (height) => Math.round(height * 0.65),
    ),
    rowGap: 0.4,
    // Small, a table wants most of the tile: a stack of rules down the middle
    // of it reads as a table, and a short one reads as nothing.
    rowsWidth: "92%",
    // Still until something asks for it: a page of report cards all breathing
    // at once is a page nobody can read. Hovering one sets its play state.
    plays: "paused",
  },
  /** Bars spread across the stage at full size, and stand together small. */
} as const;

export type PresentationPreviewScale = keyof typeof PREVIEW_SCALE;

/** Where a drawing stands: across its frame, in the middle of it. */
const PREVIEW_STAGE_CLASS = "w-full flex flex-col items-center justify-center";

/** Whether a drawing moves: a hover on a report tile sets it going (``ReportObject``). */
const PLAYS_CLASS = {
  running: "[animation-play-state:running]",
  paused: "[animation-play-state:paused]",
} as const;

function PairingPreviewFrame({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        "relative w-full h-14 pl-2 pr-2 flex items-center justify-center overflow-hidden text-secondary",
        // Its edges fade into the card it is on, and into the card's hover.
        "after:content-[''] after:absolute after:inset-0 after:pointer-events-none after:[transition:box-shadow_0.1s_ease]",
        "after:[box-shadow:inset_0_8px_12px_-6px_var(--card),inset_0_-6px_10px_-6px_var(--card),inset_8px_0_12px_-8px_var(--card),inset_-8px_0_12px_-8px_var(--card)]",
        "[.pairing-option-card:hover_&]:after:[box-shadow:inset_0_8px_12px_-6px_var(--muted),inset_0_-6px_10px_-6px_var(--muted),inset_8px_0_12px_-8px_var(--muted),inset_-8px_0_12px_-8px_var(--muted)]",
      )}
    >
      {children}
    </div>
  );
}

function PreviewBar({
  delay,
  height,
  scale,
  ink = "currentColor",
}: {
  delay: number;
  height: number;
  scale: (typeof PREVIEW_SCALE)[PresentationPreviewScale];
  ink?: string;
}) {
  return (
    <div
      className={cn(
        "shrink-0 rounded origin-bottom",
        "[animation-name:pairingPreviewPulse] [animation-duration:2.8s] [animation-timing-function:ease-in-out] [animation-iteration-count:infinite]",
        PLAYS_CLASS[scale.plays],
      )}
      style={{
        width: scale.stroke,
        height,
        // The ink is whatever the drawing is being written in, so a picker
        // gets the soft neutral it always had and a report tile gets the
        // colours its chart is drawn in, bar by bar.
        backgroundColor: ink,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

function PreviewRow({
  delay,
  indent = 0,
  width = "72%",
  scale,
}: {
  delay: number;
  indent?: number;
  width?: string | number;
  scale: (typeof PREVIEW_SCALE)[PresentationPreviewScale];
}) {
  return (
    <div
      className={cn(
        "shrink-0 rounded bg-[currentColor]",
        "[animation-name:pairingPreviewFade] [animation-duration:2.8s] [animation-timing-function:ease-in-out] [animation-iteration-count:infinite]",
        PLAYS_CLASS[scale.plays],
      )}
      style={{
        height: scale.stroke,
        width,
        // In the theme's steps of eight pixels.
        marginLeft: indent * 8,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

/**
 * A chart of the kind named, drawn in shapes.
 *
 * Bars, a line, two slices of a pie, indented rows - enough of the thing for
 * somebody to recognise it before there is any data to put in it. Written in
 * ``currentColor`` and sized by ``scale``, so the same drawing serves the
 * picker that offers a presentation and the stamp-sized tile on a report card
 * that says which one was chosen.
 */
export function ReportsViewWidgetPresentationPreview({
  presentation,
  scale: scaleName = "full",
  palette,
}: {
  presentation: string | null | undefined;
  scale?: PresentationPreviewScale;
  /**
   * The colours a real chart of this kind would be drawn in, taken in order.
   * Without one the drawing is written in ``currentColor``, which is what a
   * table or a figure wants: those carry no series to colour.
   */
  palette?: readonly string[];
}) {
  const scale = PREVIEW_SCALE[scaleName];
  const ink = (index: number) =>
    palette?.length ? palette[index % palette.length] : "currentColor";

  switch (presentation) {
    case "BAR_CHART":
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <div
            className={cn(
              "w-full flex items-end",
              scaleName === "compact"
                ? "justify-center gap-0.5"
                : "justify-between",
            )}
          >
            {scale.bars.map((height, index) => (
              <PreviewBar
                key={index}
                delay={index * 0.045}
                height={height}
                scale={scale}
                ink={ink(index)}
              />
            ))}
          </div>
        </div>
      );

    case "LINE_CHART":
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <svg
            viewBox="0 0 120 40"
            className={cn(
              "w-full h-auto shrink-0 overflow-visible",
              scaleName === "compact" ? "max-h-6" : "max-h-10",
            )}
          >
            <polyline
              className={cn(
                "pairing-preview-line [fill:none] [stroke-linecap:round] [stroke-linejoin:round]",
                scaleName === "compact"
                  ? "[stroke-width:3]"
                  : "[stroke-width:1.5]",
                "[animation-name:pairingLineFade] [animation-duration:2.8s] [animation-timing-function:ease-in-out] [animation-iteration-count:infinite]",
                PLAYS_CLASS[scale.plays],
              )}
              style={{ stroke: ink(0) }}
              points="4,34 28,24 52,28 76,12 100,18 116,8"
            />
          </svg>
        </div>
      );

    case "PIE_CHART":
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <svg
            viewBox="0 0 40 40"
            preserveAspectRatio="xMidYMid meet"
            className={cn(
              "shrink-0 block",
              "[animation-name:pairingPieFade] [animation-duration:2.8s] [animation-timing-function:ease-in-out] [animation-iteration-count:infinite]",
              PLAYS_CLASS[scale.plays],
            )}
            style={{ width: scale.pie, height: scale.pie }}
          >
            {/* Two slices of the one ink: the smaller cut lighter, so the
                pie reads as divided rather than as a disc. */}
            <path
              d="M 20 20 L 20 3 A 17 17 0 0 1 36 26 Z"
              fill={ink(1)}
              opacity={palette?.length ? 1 : 0.55}
            />
            <path d="M 20 20 L 36 26 A 17 17 0 1 1 20 3 Z" fill={ink(0)} />
          </svg>
        </div>
      );

    case "GROUPED_SUMMARY":
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <div
            className={cn(
              "flex flex-col items-start",
              scale.rowGap === 0.35
                ? "gap-[2.8px]"
                : scale.rowGap === 0.4
                  ? "gap-[3.2px]"
                  : "",
              scale.rowsWidth === "72%"
                ? "w-[72%]"
                : scale.rowsWidth === "92%"
                  ? "w-[92%]"
                  : "",
            )}
          >
            <PreviewRow delay={0} width="42%" scale={scale} />
            <div
              // Stretched to the group's own width: left to size itself to
              // its contents, a box holding nothing but percentages is a box
              // of nothing, and the rows under the heading never drew.
              className={cn(
                "self-stretch pl-1.5 flex flex-col",
                scale.rowGap === 0.35
                  ? "gap-[2.8px]"
                  : scale.rowGap === 0.4
                    ? "gap-[3.2px]"
                    : "",
              )}
            >
              <PreviewRow delay={0.25} width="68%" scale={scale} />
              <PreviewRow delay={0.45} width="54%" scale={scale} />
            </div>
          </div>
        </div>
      );

    case "KPI_STRIP":
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <div
            // A figure is a block rather than another rule, but only just:
            // at three strokes deep it was twice the size of the charts
            // beside it.
            className={cn(
              scaleName === "compact" ? "w-[40%]" : "w-[72%]",
              scaleName === "compact"
                ? "h-1.5"
                : scale.stroke === 5
                  ? "h-1.25"
                  : scale.stroke === 6
                    ? "h-1.5"
                    : "",
              "rounded bg-[currentColor]",
              "[animation-name:pairingPreviewPulse] [animation-duration:2.8s] [animation-timing-function:ease-in-out] [animation-iteration-count:infinite]",
              PLAYS_CLASS[scale.plays],
            )}
          />
        </div>
      );

    case "TREE":
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <div
            className={cn(
              "flex flex-col items-start",
              scale.rowGap === 0.35
                ? "gap-[2.8px]"
                : scale.rowGap === 0.4
                  ? "gap-[3.2px]"
                  : "",
              scale.rowsWidth === "72%"
                ? "w-[72%]"
                : scale.rowsWidth === "92%"
                  ? "w-[92%]"
                  : "",
            )}
          >
            <PreviewRow delay={0} width="88%" scale={scale} />
            <PreviewRow delay={0.15} indent={1.25} width="72%" scale={scale} />
            <PreviewRow delay={0.3} indent={2.5} width="58%" scale={scale} />
            <PreviewRow delay={0.45} indent={1.25} width="76%" scale={scale} />
          </div>
        </div>
      );

    case "TABLE":
    default:
      return (
        <div className={PREVIEW_STAGE_CLASS}>
          <div
            className={cn(
              "flex flex-col",
              scale.rowGap === 0.35
                ? "gap-[2.8px]"
                : scale.rowGap === 0.4
                  ? "gap-[3.2px]"
                  : "",
              scale.rowsWidth === "72%"
                ? "w-[72%]"
                : scale.rowsWidth === "92%"
                  ? "w-[92%]"
                  : "",
            )}
          >
            <PreviewRow delay={0} width="100%" scale={scale} />
            <PreviewRow delay={0.15} width="100%" scale={scale} />
            <PreviewRow delay={0.3} width="100%" scale={scale} />
            <PreviewRow delay={0.45} width="100%" scale={scale} />
          </div>
        </div>
      );
  }
}

function pairingGridLayout(count: number) {
  const columns = count <= 1 ? 1 : count <= 4 ? 2 : 3;
  const rows = Math.ceil(count / columns);
  return { columns, rows };
}

export function ReportsViewWidgetPairingPicker({
  options,
  onSelect,
}: {
  options: ReportsViewWidgetPairingOption[];
  onSelect: (id: string) => void;
}) {
  const { columns, rows } = pairingGridLayout(options.length);

  return (
    <div className="flex-1 min-h-0 h-full w-full flex flex-col p-1.5 overflow-hidden">
      <div
        className="flex-1 min-h-0 min-w-0 grid grid-cols-(--grid-template-columns) grid-rows-(--grid-template-rows) gap-2.5 w-full h-full"
        style={
          {
            "--grid-template-columns": `repeat(${columns}, minmax(0, 1fr))`,
            "--grid-template-rows": `repeat(${rows}, minmax(0, 1fr))`,
          } as CSSProperties
        }
      >
        {options.map((option) => (
          <button
            type="button"
            key={option.id}
            className="pairing-option-card flex flex-col items-center justify-center min-w-0 min-h-0 w-full h-full p-2 rounded-md [border-width:1px] [border-style:solid] border-border bg-card cursor-pointer text-center overflow-clip [transition:border-color_0.1s_ease,background-color_0.1s_ease,box-shadow_0.1s_ease] hover:border-ring hover:bg-muted hover:shadow-sm [&:focus-visible]:[outline-style:solid] focus-visible:[outline-width:2px] focus-visible:outline-ring focus-visible:outline-offset-2"
            onClick={() => onSelect(option.id)}
          >
            <div className="flex flex-col items-center justify-center gap-1.5 w-full">
              <div className="flex flex-col items-center gap-px">
                <Typography level="title-sm" className="[line-height:1.2]">
                  {option.label}
                </Typography>
                <Typography
                  level="body-xs"
                  className="text-muted-foreground/80 [line-height:1.2]"
                >
                  {option.description}
                </Typography>
              </div>
              <PairingPreviewFrame>
                <ReportsViewWidgetPresentationPreview
                  presentation={option.id}
                />
              </PairingPreviewFrame>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
